import { useState, useEffect, useCallback } from 'react';
import { 
  fetchPurchaseById, 
  createPurchase, 
  updatePurchase, 
  generatePurchaseReference 
} from '../controller/purchase-controller';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';


export const usePurchaseForm = (navigate, isEdit = false) => {
  const [formData, setFormData] = useState({
    reference: '',
    dateBonAchat: format(new Date(), "yyyy-MM-dd'T'HH:mm"), // ✅ Change from datePurchase to dateBonAchat
    supplierId: '', // Make sure this exists in the initial state
    depotId: '',
    supplier: {
      id: null, // Add this to explicitly store supplier ID in the supplier object
      name: '',
      phone: '',
      email: '',
      address: ''
    }
  });

  const { id } = useParams();
  
  const [orderItems, setOrderItems] = useState([]);
  const [totals, setTotals] = useState({
    subtotalHT: 0,
    remise: 0,
    tva: 0,
    total: 0
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(isEdit ? true : false);
  useEffect(() => {
    const initializePurchase = async () => {
      try {
        setIsLoading(true);
        
        if (id) { // Using id from params instead of purchaseId param
          // Fetch existing purchase
          const purchase = await fetchPurchaseById(id);
          
          // Make sure the supplier ID is correctly set in both places
          const supplierId = purchase.entet?.fournisseurId || null;
          
          // Set form data from purchase
          setFormData({
            reference: purchase.entet?.reference || '',
            dateBonAchat: purchase.entet?.dateBonCommande || format(new Date(), "yyyy-MM-dd'T'HH:mm"), // ✅ Use dateBonAchat
            supplierId: supplierId, // Set the top-level supplier ID
            depotId: purchase.entet?.depotId?.toString() || '',
            supplier: {
              id: supplierId, // Also set it in the supplier object
              name: purchase.entet?.fournisseur?.name || '',
              phone: purchase.entet?.fournisseur?.tel || '', // Matching API field names
              email: purchase.entet?.fournisseur?.email || '',
              address: purchase.entet?.fournisseur?.adresse || '' // Matching API field names
            }
          });
          
          if (Array.isArray(purchase.purchaseItems)) {
            const items = purchase.purchaseItems.map(item => ({
              id: item.id,
              productId: item.productId,
              name: item.product?.name || 'Produit inconnu',
              reference: item.product?.reference || '',
              price: parseFloat(item.price),
              quantity: item.quantity,
              tva: parseFloat(item.tva || 0),
              remise: parseFloat(item.remise || 0),
              dbId: item.id // Save the database ID for update operations
            }));
            
            setOrderItems(items);
            calculateTotals(items);
          }
        } else {
          // For new purchase, generate a reference
          try {
            const reference = await generatePurchaseReference();
            setFormData(prev => ({
              ...prev,
              reference: reference || ''
            }));
          } catch (error) {
            console.error('Error generating purchase reference:', error);
          }
        }
      } catch (error) {
        console.error('Error initializing purchase:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    initializePurchase();
  }, [id]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested fields
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Add item to order
  const handleAddItem = (item) => {
    try {
      // Create a new array with the existing items plus the new item
      const updatedItems = [...orderItems, item];
      
      // Set the state with the new array
      setOrderItems(updatedItems);
      
      // Calculate totals with the updated array, not the stale orderItems reference
      calculateTotals(updatedItems);
      
    } catch (error) {
      console.error("Error in handleAddItem:", error);
    }
  };

  // Update item in order
  const handleUpdateItem = (id, updatedItemData) => {
    
    // Create a new array with the updated item
    const updatedItems = orderItems.map(item => {
      if (item.id === id) {
        return { ...item, ...updatedItemData };
      }
      return item;
    });
    
    // Set the state with the new array
    setOrderItems(updatedItems);
    
    // Calculate totals with the updated array
    calculateTotals(updatedItems);
  };

  // Remove item from order
  const handleRemoveItem = (itemId) => {
    // Filter out the item with the given ID
    const updatedItems = orderItems.filter(item => item.id !== itemId);
  
    console.log('Updated items after removal:', updatedItems);
  
    // Update the state with the filtered array
    setOrderItems(updatedItems);
  
    // Recalculate totals with the updated array
    calculateTotals(updatedItems);
  };

  // Calculate totals
  const calculateTotals = useCallback((items = []) => {
    const itemsToCalculate = Array.isArray(items) ? items : [];
    
    if (itemsToCalculate.length === 0) {
      setTotals({
        subtotalHT: 0,
        remise: 0,
        tva: 0,
        total: 0
      });
      return;
    }
    
    let subtotalHT = 0;
    let remiseTotal = 0;
    let tvaTotal = 0;
    
    itemsToCalculate.forEach(item => {
      // Calculate HT price
      const unitHtPrice = item.price / (1 + item.tva / 100);
      
      // Calculate item totals
      const itemHT = unitHtPrice * item.quantity;
      subtotalHT += itemHT;
      
      // Calculate discount
      const itemRemise = itemHT * (item.remise / 100);
      remiseTotal += itemRemise;
      
      // Calculate TVA
      const itemTva = itemHT * (item.tva / 100);
      tvaTotal += itemTva;
    });
    
    // Calculate total
    const subtotalHTAfterRemise = subtotalHT - remiseTotal;
    const total = subtotalHTAfterRemise + tvaTotal;
    
    setTotals({
      subtotalHT,
      remise: remiseTotal,
      tva: tvaTotal,
      total
    });
  }, []);

  // Update totals directly (used by components)
  const updateTotals = (items) => {
    console.log("usePurchaseForm - updateTotals called with items:", items);
    
    if (!items || items.length === 0) {
      console.log("usePurchaseForm - No items, resetting totals to zero");
      setTotals({
        subtotalHT: 0,
        remise: 0,
        tva: 0,
        total: 0
      });
      return;
    }

    let subtotalHT = 0;
    let remiseTotal = 0;
    let tvaTotal = 0;

    items.forEach((item, index) => {
      const quantity = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.prix) || parseFloat(item.price) || 0;
      const discount = parseFloat(item.discount) || parseFloat(item.remise) || 0;
      const taxRate = parseFloat(item.taxRate) || parseFloat(item.tva) || 0;

      console.log(`usePurchaseForm - Item ${index}:`, {
        quantity,
        price,
        discount,
        taxRate
      });

      // Calculate HT price (same logic as calculateTotals)
      const unitHtPrice = price / (1 + taxRate / 100);
      
      // Calculate item totals
      const itemHT = unitHtPrice * quantity;
      subtotalHT += itemHT;
      
      // Calculate discount
      const itemRemise = itemHT * (discount / 100);
      remiseTotal += itemRemise;
      
      // Calculate TVA
      const itemTva = itemHT * (taxRate / 100);
      tvaTotal += itemTva;
    });

    // Calculate total (same logic as calculateTotals)
    const subtotalHTAfterRemise = subtotalHT - remiseTotal;
    const total = subtotalHTAfterRemise + tvaTotal;

    const newTotals = {
      subtotalHT: Number(subtotalHT.toFixed(2)),
      remise: Number(remiseTotal.toFixed(2)),
      tva: Number(tvaTotal.toFixed(2)),
      total: Number(total.toFixed(2))
    };

    console.log("usePurchaseForm - Setting new totals:", newTotals);
    setTotals(newTotals);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    
    if (!formData.supplierId && !formData.supplier.name) {
      newErrors['supplierId'] = 'Le fournisseur est requis';
    }
    
    if (!formData.reference) {
      newErrors['reference'] = 'La référence est requise';
    }
    
    if (orderItems.length === 0) {
      newErrors['items'] = 'Au moins un article est requis';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Prepare purchase data in a similar format to order data
  const preparePurchaseData = () => {
    // Prepare purchase header (entet)
    console.log('Preparing purchase data...');
    console.log('Form Data:', formData);
    const entet = {
      dateBonCommande: formData.dateBonAchat || formData.datePurchase, // ✅ Use the correct field name
      reference: formData.reference,
      totalTTC: Number(totals.total.toFixed(2)),
      totalHt: Number((totals.subtotalHT - totals.remise).toFixed(2)),
      totalTva: Number(totals.tva.toFixed(2)),
      totalRemise: Number(totals.remise.toFixed(2)),
      source: "web_admin",
      fournisseurId: formData.supplierId ? parseInt(formData.supplierId, 10) : null,
      depotId: formData.depotId ? parseInt(formData.depotId, 10) : null,
    };

    // If editing, include the purchase ID
    if (isEdit) {
      entet.id = parseInt(id, 10);
    }

    // Update the line item preparation in preparePurchaseData function
    const line = orderItems.map(item => {
      // Calculate the unit HT price using the formula
      const unitHtPrice = item.price / (1 + item.tva / 100);
      
      // Calculate HT total for this item
      const itemHT = unitHtPrice * item.quantity;
      
      // Calculate discount on the HT price
      const itemRemise = itemHT * (item.remise / 100);
      
      // Calculate TVA on the HT price (not affected by remise)
      const itemTva = itemHT * (item.tva / 100);
      
      // Calculate subtotal HT after remise
      const itemHTAfterRemise = itemHT - itemRemise;
      
      // Calculate final TTC
      const itemTTC = itemHTAfterRemise + itemTva;
      
      // Create the base line item
      const lineItem = {
        // If item has variantId, use it as productVariantId, otherwise use productId
        productId: parseInt(item.productId, 10) || null,
        productVariantId: item.variantId ? parseInt(item.variantId, 10) : null,
        quantite: parseInt(item.quantity, 10),
        prixUnitaire: Number(item.price.toFixed(2)),
        totalTTC: Number(itemTTC.toFixed(2)),
        totalHt: Number(itemHTAfterRemise.toFixed(2)), // HT after remise
        totalTva: Number(itemTva.toFixed(2)),
        totalRemise: Number(itemRemise.toFixed(2)),
        tauxTVA: Number(item.tva), // TVA rate for the item
      };

      // If editing and item has a database ID, include it
      if (isEdit && item.dbId) {
        lineItem.id = parseInt(item.dbId, 10);
      }

      return lineItem;
    });

    return {
      entet,
      line
    };
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Prepare data for API using the new structure
      const purchaseData = preparePurchaseData();
      // Save purchase
      if (isEdit) {
        purchaseData.entet.id = id;
        await createPurchase(purchaseData);
      } else {
        await createPurchase(purchaseData);
      }
      
      // Redirect to purchases list
      window.location.href = '/admin/purchases';
    } catch (error) {
      console.error('Error saving purchase:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    formData,
    setFormData, // Add this to expose the setter
    purchaseItems: orderItems, // Rename orderItems to purchaseItems for consistency
    setPurchaseItems: setOrderItems, // Expose the setter with the renamed variable
    totals,
    errors,
    isLoading,
    handleInputChange,
    handleAddItem,
    handleUpdateItem,
    handleRemoveItem,
    handleSubmit,
    updateTotals
  };
};